import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { createTenant, getAccounts, getCountries, getCurrencies, getPlans, getTaxRegimes, provisionTenant } from '@/services'
import { AppButton } from '@/components/platform/primitives'

export function NewTenantPage() {
  const [slug, setSlug] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [countryCode, setCountryCode] = useState('AR')
  const [taxRegimeId, setTaxRegimeId] = useState('')
  const [accountId, setAccountId] = useState<string>('')
  const [planId, setPlanId] = useState<string>('')
  const [themeId, setThemeId] = useState<string>('perfumeria')
  const [currencyId, setCurrencyId] = useState<string>('')
  const [customDomain, setCustomDomain] = useState<string>('')
  const [tenantVersion, setTenantVersion] = useState<string>('')

  const [result, setResult] = useState<null | {
    urls: { storefront: string; web?: string; admin: string; api: string }
    admin: { email: string; password: string }
    tenantId: string
  }>(null)

  const accountsQ = useQuery({ queryKey: ['accounts', 'select'], queryFn: () => getAccounts({ pageSize: 100 }) })
  const plansQ = useQuery({ queryKey: ['plans', 'select'], queryFn: () => getPlans() })
  const countriesQ = useQuery({ queryKey: ['countries'], queryFn: () => getCountries() })
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: () => getCurrencies() })
  const selectedCountry = (countriesQ.data || []).find((country) => country.code === countryCode)
  const taxRegimesQ = useQuery({
    queryKey: ['tax-regimes', selectedCountry?.id],
    queryFn: () => getTaxRegimes(selectedCountry?.id),
    enabled: Boolean(selectedCountry?.id),
  })

  useEffect(() => {
    const firstCountryCode = countriesQ.data?.[0]?.code
    if (!firstCountryCode || countryCode) return
    setCountryCode(firstCountryCode)
  }, [countriesQ.data, countryCode])

  useEffect(() => {
    const defaultRegime = (taxRegimesQ.data || []).find((regime) => regime.is_default) || taxRegimesQ.data?.[0]
    if (!defaultRegime) { if (taxRegimeId) setTaxRegimeId(''); return }
    if (!taxRegimeId || !taxRegimesQ.data?.some((regime) => regime.id === taxRegimeId)) setTaxRegimeId(defaultRegime.id)
  }, [taxRegimeId, taxRegimesQ.data])

  const canSubmit = useMemo(() => slug.trim().length > 0 && businessName.trim().length > 0, [slug, businessName])

  const createAndProvision = useMutation({
    mutationFn: async () => {
      const tenant = await createTenant({
        slug: slug.trim().toLowerCase(),
        business_name: businessName.trim(),
        country_code: countryCode.trim().toUpperCase(),
        currency_id: currencyId || undefined,
        tax_regime_id: taxRegimeId || undefined,
        account_id: accountId || null,
        plan_id: planId || null,
        theme_id: themeId || 'perfumeria',
        custom_domain: customDomain.trim() || null,
        tenant_version: tenantVersion.trim() || undefined,
      })
      const provision = await provisionTenant(tenant.id)
      return { tenant, provision }
    },
    onSuccess: ({ tenant, provision }) => {
      toast.success('Tenant creado y provisionado')
      setResult({ urls: provision.urls, admin: provision.admin, tenantId: tenant.id })
    },
    onError: (err: any) => toast.error(err?.message || 'Error creando/provisionando tenant'),
  })

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="row gap-2" style={{ marginBottom: 6 }}>
            <Link to="/tenants" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>← Tenants</Link>
          </div>
          <h1>Nuevo tenant</h1>
          <div className="subtitle">Crea un cliente + tenant stack listo para usar (admin + storefront + API).</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>Datos del tenant</h3></div>
        <div className="card-body col gap-4">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-row">
              <label htmlFor="nt-slug">Slug</label>
              <input id="nt-slug" name="slug" className="input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="cliente-1" />
              <span className="hint">Se usa en URLs por path: /&lt;slug&gt;</span>
            </div>
            <div className="form-row">
              <label htmlFor="nt-name">Nombre</label>
              <input id="nt-name" name="business_name" className="input" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Cliente 1" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            <div className="form-row">
              <label htmlFor="nt-country">País</label>
              <select id="nt-country" className="select" value={countryCode} onChange={(e) => { setCountryCode(e.target.value); setTaxRegimeId(''); setCurrencyId('') }}>
                {(countriesQ.data || []).map((country) => <option key={country.id} value={country.code}>{country.name}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="nt-currency">Moneda</label>
              <select id="nt-currency" className="select" value={currencyId} onChange={(e) => setCurrencyId(e.target.value)}>
                <option value="">Moneda del país ({selectedCountry?.currency_code || '—'})</option>
                {(currenciesQ.data || []).map((cur) => <option key={cur.id} value={cur.id}>{cur.symbol} {cur.code} — {cur.name}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="nt-domain">Dominio propio (opcional)</label>
              <input id="nt-domain" name="custom_domain" className="input" value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} placeholder="cliente.com" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
            <div className="form-row">
              <label htmlFor="nt-regime">Régimen fiscal</label>
              <select id="nt-regime" className="select" value={taxRegimeId} onChange={(e) => setTaxRegimeId(e.target.value)} disabled={taxRegimesQ.isLoading}>
                <option value="">Seleccionar régimen</option>
                {(taxRegimesQ.data || []).map((regime) => <option key={regime.id} value={regime.id}>{regime.name}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="nt-theme">Theme</label>
              <select id="nt-theme" className="select" value={themeId} onChange={(e) => setThemeId(e.target.value)}>
                <option value="perfumeria">Perfumería</option>
                <option value="ropa">Ropa</option>
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="nt-account">Cliente (account)</label>
              <select id="nt-account" className="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                <option value="">(Sin cliente)</option>
                {(accountsQ.data?.data || []).map((a) => <option key={a.id} value={a.id}>{a.name} ({a.email})</option>)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="nt-plan">Plan</label>
              <select id="nt-plan" className="select" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                <option value="">(Sin plan)</option>
                {(plansQ.data || []).map((p) => <option key={p.id} value={p.id}>{p.name} ({p.slug})</option>)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="nt-version">Versión del stack (opcional)</label>
              <input id="nt-version" name="tenant_version" className="input" value={tenantVersion} onChange={(e) => setTenantVersion(e.target.value)} placeholder="1.0.6" />
            </div>
          </div>

          <div>
            <AppButton variant="primary" icon="check" onClick={() => createAndProvision.mutate()} disabled={!canSubmit || createAndProvision.isPending}>
              {createAndProvision.isPending ? 'Creando…' : 'Crear y provisionar'}
            </AppButton>
          </div>
        </div>
      </div>

      {result ? (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header"><h3>Listo</h3></div>
          <div className="card-body">
            <dl className="dl" style={{ gridTemplateColumns: '120px 1fr' }}>
              <dt>Storefront</dt><dd>{result.urls.storefront}</dd>
              {result.urls.web ? <><dt>Web</dt><dd>{result.urls.web}</dd></> : null}
              <dt>Admin</dt><dd>{result.urls.admin}</dd>
              <dt>API</dt><dd>{result.urls.api}</dd>
              <dt>Email admin</dt><dd>{result.admin.email}</dd>
              <dt>Password</dt><dd><span className="code-chip">{result.admin.password}</span></dd>
              <dt>Tenant ID</dt><dd><span className="code-chip">{result.tenantId}</span></dd>
            </dl>
          </div>
        </div>
      ) : null}
    </>
  )
}
